import type { StatusOption, Tone } from '@hemmafixa/ui';
import { useMemo } from 'react';
import { useI18n } from '@/i18n/context';
import type {
  InvoiceStatus,
  JobStatus,
  ProposalStatus,
  ProviderStatus,
  SubscriptionStatus,
  UserStatus,
} from '@hemmafixa/shared';

const jobTones: Record<JobStatus, Tone> = {
  draft: 'neutral',
  in_review: 'warning',
  needs_info: 'brand',
  open: 'info',
  provider_chosen: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
  expired: 'neutral',
};
const proposalTones: Record<ProposalStatus, Tone> = {
  submitted: 'info',
  accepted: 'brand',
  declined: 'neutral',
  withdrawn: 'neutral',
  chosen: 'success',
  closed: 'neutral',
};
const providerTones: Record<ProviderStatus, Tone> = {
  onboarding: 'neutral',
  pending: 'warning',
  verified: 'success',
  rejected: 'danger',
};
const userTones: Record<UserStatus, Tone> = { active: 'success', suspended: 'danger' };
const subscriptionTones: Record<SubscriptionStatus, Tone> = {
  incomplete: 'neutral',
  trialing: 'info',
  active: 'success',
  past_due: 'danger',
  canceled: 'neutral',
};
const invoiceTones: Record<InvoiceStatus, Tone> = {
  draft: 'neutral',
  open: 'warning',
  paid: 'success',
  uncollectible: 'danger',
  void: 'neutral',
};

function withLabels<T extends string>(tones: Record<T, Tone>, labels: Record<T, string>) {
  return Object.fromEntries(
    Object.entries(tones).map(([status, tone]) => [status, { tone, label: labels[status as T] }]),
  ) as Record<T, StatusOption>;
}

/** Status maps for StatusBadge, so a status has the same colour and text on every screen. */
export function useStatuses() {
  const { t } = useI18n();
  return useMemo(
    () => ({
      job: withLabels(jobTones, t.status.job),
      proposal: withLabels(proposalTones, t.status.proposal),
      provider: withLabels(providerTones, t.status.provider),
      user: withLabels(userTones, t.status.user),
      subscription: withLabels(subscriptionTones, t.status.subscription),
      invoice: withLabels(invoiceTones, t.status.invoice),
    }),
    [t],
  );
}
